import type { BusinessProfile } from "../../interfaces/BusinessObject";

/** Where a business stands on the autopay onboarding gate. */
export type AutopayReadiness = "in_progress" | "review" | "ready";

/**
 * Autopay readiness gate.
 *
 * Autopay opens only once the owner has signed off the business page. That maps
 * onto the three states of `isOnboarded` (see BusinessProfile.isOnboarded):
 *
 *  - `null` / `undefined` → "in_progress": the page is still being set up. The
 *    flag is only switched on at hand-over, so until then it stays null.
 *  - `false`              → "review": handed over, waiting for the owner to
 *    review it (the "Review it" prompt on the business page).
 *  - `true`               → "ready": reviewed, autopay can be set up.
 *
 * Call sites only ask this where autopay is about to be started or resumed
 * (no subscription yet, EXPIRING/LAPSED restarts, ACTION_REQUIRED). A business
 * with an active/authenticated subscription never reaches those paths, so an
 * unreviewed page never blocks one that is already paying.
 *
 * Returns `null` when the business isn't known yet (not loaded) so callers don't
 * lock the UI on missing data — the backend enforces the same rule regardless.
 */
export function getAutopayReadiness(
  business?: Pick<BusinessProfile, "isOnboarded"> | null
): AutopayReadiness | null {
  if (!business) return null;
  if (business.isOnboarded === true) return "ready";
  if (business.isOnboarded === false) return "review";
  return "in_progress";
}

/** True when the business is known and autopay must not start yet. */
export function isAutopayLocked(
  business?: Pick<BusinessProfile, "isOnboarded"> | null
): boolean {
  const readiness = getAutopayReadiness(business);
  return readiness === "in_progress" || readiness === "review";
}

export const REVIEW_PENDING_MESSAGE =
  "Please review your business page before setting up autopay.";

export const ONBOARDING_IN_PROGRESS_MESSAGE =
  "Your business page is still being set up by our team. Autopay will be available once it is ready for your review.";

/** Short status label for the in-progress state. */
export const ONBOARDING_IN_PROGRESS_LABEL = "Onboarding in progress";

/** Longer explanation shown next to the label. */
export const ONBOARDING_IN_PROGRESS_NOTE =
  "Our team is setting up your business page. You'll be able to review it and set up autopay once it's ready.";

export function lockedMessageFor(readiness: AutopayReadiness | null): string {
  return readiness === "review"
    ? REVIEW_PENDING_MESSAGE
    : ONBOARDING_IN_PROGRESS_MESSAGE;
}
